import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { mapi } from './api';
import { Button, ButtonRow, Field, Grid2, Hint, Muted, NeutralChip, Warn } from '../ui/primitives';
import type { CustomerRow, CustomerUser } from './types';
import { Osio } from './Osio';

/* Myyjätunnukset luodaan masterista, koska uusi asiakas ei pääse luomaan
   itselleen ensimmäistä tunnusta ennen kuin ohjelma on kerran avattu.
   Kirjoitus menee asiakkaan omaan kantaan samalla salasanatiivisteellä kuin
   asiakassovelluksessa (src/auth.js) — ei toista toteutusta. */

const Rivi = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 0;
  border-bottom: 1px solid ${p => p.theme.color.line};
  &:last-of-type { border-bottom: 0; }
  .nimi { font-weight: 600; }
  .tiedot { color: ${p => p.theme.color.muted}; font-size: 12.5px; }
  .napit { margin-left: auto; display: flex; gap: 6px; }
`;

interface Props {
  asiakas: CustomerRow;
  onToast: (v: string) => void;
  onChanged: () => void;
}

export function UsersCard({ asiakas, onToast, onChanged }: Props) {
  const [users, setUsers] = useState<CustomerUser[] | null>(null);
  const [saatavilla, setSaatavilla] = useState(true);
  const [lisaa, setLisaa] = useState(false);
  const [nimi, setNimi] = useState('');
  const [salasana, setSalasana] = useState('');
  const [yllapitaja, setYllapitaja] = useState(true);
  const [tyossa, setTyossa] = useState(false);

  const lataa = useCallback(async () => {
    try {
      const r = await mapi<{ saatavilla: boolean; users: CustomerUser[] }>(
        `/api/master/customers/${asiakas.id}/users`);
      setSaatavilla(r.saatavilla);
      setUsers(r.users);
    } catch { setUsers([]); }
  }, [asiakas.id]);

  useEffect(() => { void lataa(); }, [lataa]);

  const luoKanta = async () => {
    if (!confirm(`Luodaanko tietokanta polkuun\n${asiakas.db_path}?\n\n`
      + 'Jos polku on kansio, kanta tulee sen alle (data/myynti.db). '
      + 'Olemassa olevaa kantaa ei ylikirjoiteta.')) return;
    setTyossa(true);
    try {
      await mapi(`/api/master/customers/${asiakas.id}/database`, 'POST', {});
      onToast('Tietokanta luotu');
      await lataa();
      onChanged();
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    setTyossa(false);
  };

  const tallennaUusi = async () => {
    setTyossa(true);
    try {
      const r = await mapi<{ users: CustomerUser[] }>(
        `/api/master/customers/${asiakas.id}/users`, 'POST',
        { name: nimi.trim(), password: salasana, isAdmin: yllapitaja });
      setUsers(r.users);
      setNimi(''); setSalasana(''); setLisaa(false);
      onToast(`Käyttäjä "${nimi.trim()}" lisätty`);
      onChanged();
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    setTyossa(false);
  };

  const vaihdaSalasana = async (u: CustomerUser) => {
    const uusi = prompt(`Uusi salasana käyttäjälle "${u.name}" (väh. 6 merkkiä):`);
    if (!uusi) return;
    try {
      const r = await mapi<{ users: CustomerUser[] }>(
        `/api/master/customers/${asiakas.id}/users/${u.id}`, 'PUT', { password: uusi });
      setUsers(r.users);
      onToast(`Salasana vaihdettu — kerro se käyttäjälle turvallista kanavaa pitkin`);
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
  };

  const poista = async (u: CustomerUser) => {
    if (!confirm(`Poistetaanko käyttäjä "${u.name}"?\n\nHänen lähettämänsä viestit ja `
      + 'tapahtumamerkinnät säilyvät.')) return;
    try {
      const r = await mapi<{ users: CustomerUser[] }>(
        `/api/master/customers/${asiakas.id}/users/${u.id}`, 'DELETE');
      setUsers(r.users);
      onToast(`Käyttäjä "${u.name}" poistettu`);
      onChanged();
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
  };

  return (
    <Osio>
      <h4>
        Myyjätunnukset
        {saatavilla
          ? <NeutralChip>{users ? `${users.length} tunnusta` : 'ladataan…'}</NeutralChip>
          : <NeutralChip>Tietokantaa ei ole</NeutralChip>}
      </h4>

      {!saatavilla && (
        <>
          <Warn>
            Asiakkaan tietokantaa ei löydy polusta <code>{asiakas.db_path}</code>.
            Luo se tästä, niin voit lisätä myyjätunnukset heti — asiakkaan ei tarvitse
            avata ohjelmaa ensin.
          </Warn>
          <ButtonRow>
            <Button $variant="primary" disabled={tyossa} onClick={luoKanta}>
              {tyossa ? 'Luodaan…' : '🗄 Luo tietokanta'}
            </Button>
          </ButtonRow>
        </>
      )}

      {saatavilla && users && !users.length && (
        <Hint>
          Ei vielä yhtään tunnusta. Ensimmäisestä käyttäjästä tulee automaattisesti
          ylläpitäjä, joka voi lisätä loput asiakkaan omassa näkymässä.
        </Hint>
      )}

      {saatavilla && users?.map(u => (
        <Rivi key={u.id}>
          <span className="nimi">{u.name}</span>
          {!!u.is_admin && <NeutralChip>ylläpitäjä</NeutralChip>}
          <span className="tiedot">
            {u.full_name ? `${u.full_name} · ` : ''}{u.email || 'ei sähköpostia'}
            {' · '}{u.sent_count} viestiä
          </span>
          <span className="napit">
            <Button onClick={() => vaihdaSalasana(u)}>Vaihda salasana</Button>
            <Button onClick={() => poista(u)}>Poista</Button>
          </span>
        </Rivi>
      ))}

      {saatavilla && !lisaa && (
        <ButtonRow>
          <Button onClick={() => setLisaa(true)}>+ Lisää myyjätunnus</Button>
        </ButtonRow>
      )}

      {saatavilla && lisaa && (
        <>
          <Grid2>
            <Field>
              <label>Käyttäjänimi</label>
              <input value={nimi} onChange={e => setNimi(e.target.value)} placeholder="etunimi" />
            </Field>
            <Field>
              <label>Salasana (väh. 6 merkkiä)</label>
              <input value={salasana} onChange={e => setSalasana(e.target.value)} />
            </Field>
          </Grid2>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}>
            <input
              type="checkbox" style={{ width: 'auto' }} checked={yllapitaja}
              onChange={e => setYllapitaja(e.target.checked)}
            />
            Ylläpitäjä (voi lisätä muita käyttäjiä asiakkaan omassa näkymässä)
          </label>
          <ButtonRow>
            <Button
              $variant="primary" onClick={tallennaUusi}
              disabled={tyossa || !nimi.trim() || salasana.length < 6}
            >
              {tyossa ? 'Tallennetaan…' : 'Luo tunnus'}
            </Button>
            <Button onClick={() => { setLisaa(false); setNimi(''); setSalasana(''); }}>
              Peruuta
            </Button>
          </ButtonRow>
          <Muted>
            Salasana näkyy tässä selvätekstisenä vain nyt — kerro se käyttäjälle ja
            pyydä vaihtamaan se omassa näkymässään.
          </Muted>
        </>
      )}
    </Osio>
  );
}
